/**
 * Resolves the Google Maps JavaScript API key from whichever runtime env the
 * consumer uses:
 *  - Vite (admin frontend) → `VITE_GOOGLE_MAPS_API_KEY`
 *  - Next.js (website) → `NEXT_PUBLIC_GOOGLE_MAPS_API_KEY`
 *
 * Returns an empty string if no key is configured. The MapView component
 * still renders a fail-soft empty container so a missing key doesn't crash
 * the host page (per Phase 2 build-must-not-hard-fail constraint while keys
 * are being provisioned in Phase 1 #322).
 *
 * Optional `mapId` resolution mirrors the same env shape — Map ID is required
 * for AdvancedMarker support but the map will silently render markers as
 * legacy pins if missing.
 */

export function getGoogleMapsApiKey(): string {
  try {
    // @ts-ignore — import.meta.env only exists under Vite
    const viteKey = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_GOOGLE_MAPS_API_KEY : undefined;
    if (viteKey) return viteKey as string;
  } catch {
    // import.meta unavailable (Next.js node context) — fall through
  }

  if (typeof process !== 'undefined' && process.env) {
    const nextKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    if (nextKey) return nextKey;
  }
  return '';
}

export function getGoogleMapsMapId(): string | undefined {
  try {
    // @ts-ignore
    const viteId = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_GOOGLE_MAPS_MAP_ID : undefined;
    if (viteId) return viteId as string;
  } catch {
    // fall through
  }
  if (typeof process !== 'undefined' && process.env) {
    const nextId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID;
    if (nextId) return nextId;
  }
  return undefined;
}
